import { ArrowsClockwiseIcon, CaretCircleLeftIcon, CaretCircleRightIcon, PlayIcon } from '@phosphor-icons/react';
import { formatTimeInHMSString } from '../../utils/formatTime';
import { useEffect, useRef, useState } from 'react';
import { cachedGet } from '../../lib/requestDedupe';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function SubjectMovement({ packId, testId, testIdTwo, isCombined = false, testsData }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const [currentTestId, setCurrentTestId] = useState(testId);
  const [tests, setTests] = useState([]);

  useEffect(() => {
    if (testsData && Array.isArray(testsData) && testsData.length > 0) {
      setTests(testsData);
      setCurrentTestId(testsData[0]?.testId);
      setSelectedTestIndex(0);
    } else {
      setCurrentTestId(testId);
    }
  }, [testsData, testId]);

  const handleTestChange = (index) => {
    setSelectedTestIndex(index);
    setCurrentTestId(tests[index]?.testId);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await cachedGet(`/api/test/analysis/v2/${packId}/${currentTestId}/subjectMovement`);
        let result = res?.data?.journeyV2;
        if (!result) {
          result = res?.data?.journeyV1;
        }
        setData(result);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [packId, currentTestId]);

  const scrollJourney = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 300;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  if (isLoading) return <TestAnalysisLoader />;

  return (
    <div className="relative">
      {isCombined && tests.length > 0 && (
        <div className="flex gap-3 mb-4 pb-0">
          {tests.map((test, index) => (
            <button
              key={index}
              onClick={() => handleTestChange(index)}
              className={`px-3 py-2 font-medium text-sm transition-colors duration-200 border border-[var(--q3-stroke-normal)] rounded-4xl cursor-pointer ${
                selectedTestIndex === index
                  ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                  : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]'
              }`}
            >
              {test.testName}
            </button>
          ))}
        </div>
      )}
      {/* Scrollable subject movement section */}
      <div className="overflow-x-auto" ref={scrollRef}>
        <div className="flex flex-row gap-10 min-w-fit relative pl-4 pt-6 pb-4">
          <style jsx>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {/* Dotted line under images */}
          <div className="absolute top-[51px] left-13 right-8 border-t-2 border-dashed border-[var(--q3-stroke-dark)] z-0" />

          {data?.map((item, index) => (
            <div key={index} className="relative flex flex-col justify-center items-center z-10">
              {/* Image */}
              <div
                className="mt-1.5 p-2 rounded-full border bg-[var(--q3-surface-default)] relative"
                style={{ borderColor: item.color }}
              >
                <img src={item.icon || '/placeholder.svg'} alt={item.title} />
                {item.isOpenedAgain && (
                  <div className="absolute bottom-0 right-0 translate-x-[4px] translate-y-[4px] bg-[var(--q3-neutral-default)] rounded-full p-[2px]">
                    <ArrowsClockwiseIcon size={14} className="text-[var(--q3-surface-default)]" />
                  </div>
                )}
              </div>

              {/* Text content */}
              <div className="text-center h-auto mt-2">
                <p className="text-[12px] text-[var(--q3-neutral-default)] font-bold line-clamp-2">{item.title}</p>
                <p className="text-[var(--q3-neutral-light)] text-[12px] whitespace-nowrap">
                  {item.questionAttemptedBeforeSwitch} Qs attempted
                </p>
                {formatTimeInHMSString(item.timeSpent) && (
                  <p className="text-[var(--q3-neutral-light)] text-[12px] font-medium whitespace-nowrap">
                    Spent {formatTimeInHMSString(item.timeSpent)}
                  </p>
                )}
              </div>

              {/* Arrow between steps */}
              {index !== data.length - 1 && (
                <div className="absolute top-[21px] right-[-22px] text-[var(--q3-stroke-dark)] text-sm">
                  <PlayIcon weight="fill" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="sticky top-full text-[var(--q3-accent-normal)] py-2 mt-2">
        <div className="flex items-center justify-center font-medium px-4">
          <CaretCircleLeftIcon
            size={30}
            className="cursor-pointer text-[var(--q3-accent-normal)]"
            onClick={() => scrollJourney('left')}
          />
          <p className="mx-4 text-sm whitespace-nowrap">Swipe left right to view full journey</p>
          <CaretCircleRightIcon
            size={30}
            className="cursor-pointer text-[var(--q3-accent-normal)]"
            onClick={() => scrollJourney('right')}
          />
        </div>
      </div>
    </div>
  );
}
